import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useAmplitude, useTrackOnMount } from '../../providers/AmplitudeProvider';
import { AmplitudeEvent } from '../../constants/amplitudeEvents';

interface WalkingExperienceProps {
    onSelect: (hasExperience: boolean) => void;
    onBack: () => void;
}

const WalkingExperience: React.FC<WalkingExperienceProps> = ({ onSelect, onBack }) => {
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const { trackEvent } = useAmplitude();

    useTrackOnMount([
        { event: AmplitudeEvent.QUESTION_VIEWED, properties: { step_number: 3, screen_name: 'experience_survey' } },
    ]);

    const handleSelect = (value: boolean) => {
        const optionId = value ? 'yes' : 'no';
        setSelectedOption(optionId);
        trackEvent(AmplitudeEvent.QUESTION_ANSWERED, {
            step_number: 3,
            screen_name: 'experience_survey',
            tried_walking_before: value,
        });
        setTimeout(() => {
            onSelect(value);
        }, 500);
    };
    return (
        <div className="min-h-screen bg-white flex flex-col relative overflow-hidden">
            <header className="py-[26px] px-4 bg-white border-b border-[#17171714]">
                <button
                    onClick={onBack}
                    className="absolute left-4 top-[26px] w-10 h-10 flex items-center justify-center rounded-full border border-[#00000066]"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex justify-center">
                    <div className="flex flex-col items-center gap-2">
                        <span className="text-[18px] text-[#0B0B0B] font-semibold">
                            My Profile
                        </span>
                        <div className="flex gap-2">
                            <div className="flex-1 w-9 h-[4px] rounded-full bg-[#369FFF]" />
                            <div className="flex-1 w-9 h-[4px] rounded-full bg-[#E5E5EA]" />
                            <div className="flex-1 w-9 h-[4px] rounded-full bg-[#E5E5EA]" />
                            <div className="flex-1 w-9 h-[4px] rounded-full bg-[#E5E5EA]" />
                            <div className="flex-1 w-9 h-[4px] rounded-full bg-[#E5E5EA]" />
                        </div>
                    </div>
                </div>
            </header>

            <div className='max-w-md lg:mx-auto px-4 relative'>
                <div className="lg:min-w-[416px] flex flex-col relative z-10 pt-6">
                    <h1 className="font-poppins text-[26px]/[43px] font-semibold text-[#0F1724] text-center mb-8">
                        Have you tried walking to<br />get in shape before?
                    </h1>

                    <div className="space-y-4 max-w-[220px]">
                        {[
                            { id: 'yes', label: 'Yes', value: true },
                            { id: 'no', label: 'No', value: false },
                        ].map((option) => {
                            const isSelected = selectedOption === option.id;
                            return (
                                <button
                                    key={option.id}
                                    onClick={() => handleSelect(option.value)}
                                    className={`w-full px-6 py-5 border rounded-2xl flex items-center justify-between transition-colors ${isSelected ? 'border-[#369FFF]' : 'border-[#E5E5EA] hover:border-[#369FFF]'}`}
                                >
                                    <span className="text-[18px] font-semibold text-[#0F1724]">{option.label}</span>
                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-[#369FFF]' : 'border-[#D1D5DB]'}`}>
                                        {isSelected && (
                                            <div className="w-3.5 h-3.5 rounded-full bg-[#369FFF]" />
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="absolute top-[66px] right-0 w-[85%] pointer-events-none">
                    <img
                        src="https://funnel-cdn.b-cdn.net/images/walking-experience-character.png"
                        alt=""
                        className="w-full h-full"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default WalkingExperience;
